import { ElementType } from 'react';

import { Flex, FlexProps, useColorModeValue } from '@bonusly/design-system';

// Polymorphic in the same way Chakra's own components are: rendering the pill as
// another element (`as={RouterLink}`) also accepts THAT element's props, so a
// navigating tab is written inline as <PillTab as={RouterLink} to={...} /> with
// no wrapper component. A plain FC<FlexProps> would reject `to`.
type PillTabProps<C extends ElementType = 'button'> = FlexProps & {
	isActive?: boolean;
	as?: C;
} & Omit<React.ComponentPropsWithoutRef<C>, keyof FlexProps | 'as' | 'isActive'>;

export const PillTab = <C extends ElementType = 'button'>({ isActive, ...rest }: PillTabProps<C>) => {
	const activeBg = useColorModeValue('secondary.bg', 'white');
	const activeColor = useColorModeValue('text.onColor', 'gray.900');

	return (
		<Flex
			as="button"
			bg={isActive ? activeBg : 'transparent'}
			borderColor={isActive ? 'transparent' : 'border.default'}
			borderRadius="full"
			borderStyle="solid"
			borderWidth="1px"
			color={isActive ? activeColor : 'text.default'}
			flexBasis="auto"
			flexShrink={1}
			fontWeight="medium"
			justifyContent="center"
			px={3}
			py={2}
			role="link"
			transitionDuration="normal"
			transitionProperty="common"
			whiteSpace="nowrap"
			{...(isActive && {
				'data-active': true,
			})}
			_hover={isActive ? { bg: activeBg, color: activeColor } : { bg: 'secondary.bgAlpha', color: 'text.default' }}
			{...rest}
		/>
	);
};
